/**
 * The cryptography behind the sign-in gate.
 *
 * Carried over from the single-file demo with the parameters unchanged:
 * PBKDF2-SHA256 at 150,000 iterations with a per-account salt for passwords,
 * 20,000 for recovery codes, and RFC 6238 TOTP on a thirty second step with a
 * one step window either side for clock drift — the same maths any
 * authenticator app does, so the codes it shows are the codes this verifies.
 *
 * All of it needs Web Crypto, which browsers only expose in a secure context.
 * `cryptoReady` is what the gate asks before offering a sign-in at all.
 */

export const cryptoReady = (): boolean =>
  typeof crypto !== "undefined" && !!crypto.subtle;

/** base64url, no padding — how the salt and hash are stored. */
export const b64u = (b: ArrayBuffer | Uint8Array): string =>
  btoa(String.fromCharCode(...new Uint8Array(b as ArrayBuffer)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

export const rnd = (n: number): Uint8Array =>
  crypto.getRandomValues(new Uint8Array(n));

export async function pbkdf2(
  pass: string,
  salt: string,
  iters: number,
): Promise<string> {
  const k = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(pass),
    "PBKDF2",
    false,
    ["deriveBits"],
  );
  return b64u(
    await crypto.subtle.deriveBits(
      {
        name: "PBKDF2",
        salt: Uint8Array.from(
          atob(salt.replace(/-/g, "+").replace(/_/g, "/")),
          (c) => c.charCodeAt(0),
        ),
        iterations: iters,
        hash: "SHA-256",
      },
      k,
      256,
    ),
  );
}

/* --- base32, for the authenticator secret ------------------------------------ */
const B32 = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";

export function toB32(bytes: Uint8Array): string {
  let bits = "",
    out = "";
  bytes.forEach((b) => (bits += b.toString(2).padStart(8, "0")));
  for (let i = 0; i + 5 <= bits.length; i += 5)
    out += B32[parseInt(bits.slice(i, i + 5), 2)];
  return out;
}

export function fromB32(s: string): Uint8Array {
  let bits = "";
  s.toUpperCase()
    .replace(/[^A-Z2-7]/g, "")
    .split("")
    .forEach((c) => (bits += B32.indexOf(c).toString(2).padStart(5, "0")));
  const out: number[] = [];
  for (let i = 0; i + 8 <= bits.length; i += 8)
    out.push(parseInt(bits.slice(i, i + 8), 2));
  return new Uint8Array(out);
}

/** RFC 6238, thirty second step, six digits — the same maths an authenticator does. */
export async function totp(secretB32: string, when?: number): Promise<string> {
  const t = Math.floor((when || Date.now()) / 1000 / 30);
  const msg = new Uint8Array(8);
  new DataView(msg.buffer).setUint32(4, t, false);
  const key = await crypto.subtle.importKey(
    "raw",
    fromB32(secretB32) as unknown as BufferSource,
    { name: "HMAC", hash: "SHA-1" },
    false,
    ["sign"],
  );
  const mac = new Uint8Array(await crypto.subtle.sign("HMAC", key, msg));
  const off = mac[mac.length - 1] & 0x0f;
  const code =
    (((mac[off] & 0x7f) << 24) |
      (mac[off + 1] << 16) |
      (mac[off + 2] << 8) |
      mac[off + 3]) %
    1000000;
  return String(code).padStart(6, "0");
}

/** One step either side for clock drift. */
export async function totpValid(
  secret: string,
  code: string,
): Promise<boolean> {
  const now = Date.now();
  for (const skew of [-1, 0, 1]) {
    if ((await totp(secret, now + skew * 30000)) === String(code).trim())
      return true;
  }
  return false;
}

/**
 * How far out a refused code is, counted in thirty second steps, or null if
 * these six digits are not this secret's at all inside the searched window.
 *
 * Two very different things look identical on the screen — the clock on the
 * phone has drifted, or the app is reading a different entry — and only a
 * wide search separates them, so the gate can say which one it is instead of
 * "not valid". This widens nothing that is accepted: `totpValid` still allows
 * one step either side. It only explains a refusal that has already happened,
 * and it searches outwards from now so the drift it reports is the smallest
 * one that accounts for the code.
 */
export async function totpDrift(
  secret: string,
  code: string,
  steps = 60,
): Promise<number | null> {
  const now = Date.now();
  const want = String(code).trim();
  for (let k = 0; k <= steps; k++) {
    for (const s of k === 0 ? [0] : [k, -k]) {
      if ((await totp(secret, now + s * 30000)) === want) return s;
    }
  }
  return null;
}

export async function hashCode(c: string, salt: string): Promise<string> {
  return pbkdf2(c.toUpperCase().replace(/\s/g, ""), salt, 20000);
}
